/** @jsxImportSource @emotion/react */
import * as React from "react";
import styled from "@emotion/styled";
import {
  Button,
  Callout,
  ContentToggle,
  EntityBadge,
  FlexRow,
  H2,
  H3,
  IconCheckRounded,
  IconX,
  Link,
  ScreenReaderText,
  Text,
} from "atoms";
import { useAuthHasRole } from "features/auth";
import { hexToRGB } from "styles/styleUtils";
import { formatDate } from "utils/date";
import { ArtistReview, GenericReview, ProjectReview } from "../types";
import { useUserReviews } from "../api/useUserReviews";
import { Review } from "./Review";
import { reviewFeedback } from "../utils";

type ReviewsByGroup = {
  [key in GenericReview["status"]]: (ProjectReview | ArtistReview)[];
};

type ProfileReviewsParams = {
  id: string;
};

function ProfileReviews({ id }: ProfileReviewsParams) {
  const { data } = useUserReviews(id);
  const isReviewer = useAuthHasRole([
    "curator-level-1",
    "curator-level-2",
    "curator-level-3",
    "admin",
  ]);

  const reviewsByGroup = React.useMemo(() => {
    const accumulator: ReviewsByGroup = {
      PENDING: [],
      PUBLISHED: [],
      REJECTED: [],
      DRAFT: [],
    };

    if (!data) return accumulator;

    return data.reduce((acc, cur) => {
      if (acc[cur.status]) {
        acc[cur.status].push(cur);
      }
      return acc;
    }, accumulator);
  }, [data]);

  const [showAllReviews, setShowAllReviews] = React.useState<boolean>(false);
  const showViewAllReviewButton = (data?.length ?? 0) > 3 && !showAllReviews;
  const displayedReviews = (showAllReviews ? data : data?.slice(0, 3)) || [];

  return (
    <>
      <FlexRow justifyContent="space-between">
        <SH2 size="xs">My Reviews</SH2>

        {(data?.length ?? 0) > 0 && (
          <div css={{ flex: "none" }}>
            <Text as="span" size="xxs">
              {reviewsByGroup.PENDING.length}&nbsp;Pending
            </Text>
            <span css={{ opacity: ".25", fontSize: ".8em", margin: "0 3px" }}>
              {" "}
              |{" "}
            </span>
            <Text as="span" size="xxs">
              {reviewsByGroup.PUBLISHED.length}&nbsp;Published
            </Text>
            <span css={{ opacity: ".25", fontSize: ".8em", margin: "0 3px" }}>
              {" "}
              |{" "}
            </span>
            <Text as="span" size="xxs">
              {reviewsByGroup.REJECTED.length}&nbsp;Rejected
            </Text>
          </div>
        )}
      </FlexRow>
      <ReviewsWrapper>
        {(data?.length ?? 0) === 0 && (
          <Callout css={{ textAlign: "center" }}>
            <H3 as="p" size="md">
              No reviews yet
            </H3>

            {isReviewer ? (
              <Link size="md" variant="gradient" arrow="dr" to="/search/">
                Explore to start writing reviews
              </Link>
            ) : (
              <Link
                size="md"
                variant="gradient"
                arrow="dr"
                to="/become-a-reviewer"
              >
                Become a Reviewer
              </Link>
            )}
          </Callout>
        )}

        <ReviewList>
          {displayedReviews.map((review: ProjectReview) => (
            <ReviewSummary status={review.status.toLowerCase()} key={review.id}>
              <SH3 size="sm">
                <Link
                  to={`/${review.reviewableType.toLowerCase()}s/${
                    review.reviewableId
                  }`}
                >
                  {review.importProjectName}
                </Link>
                <EntityBadge>{review.reviewableType.toLowerCase()}</EntityBadge>
              </SH3>
              <ReviewSummaryMain justifyContent="space-between">
                <LighterText size="xs">
                  Created on{" "}
                  {formatDate(review.createdAt, {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                  <br />
                  {review.updatedAt &&
                    `Updated on ${formatDate(review.updatedAt, {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                    })}`}
                </LighterText>
                <FlexRow css={{ flex: "none" }}>
                  <Status size="xs">{review.status.toLowerCase()}</Status>
                  <FlexRow flexWrap="nowrap" css={{ flex: "none", gap: 10 }}>
                    <SummaryUpvote>
                      <IconCheckRounded aria-hidden="true" />
                      {review.counterUpvote}
                      <ScreenReaderText>up votes</ScreenReaderText>
                    </SummaryUpvote>
                    <SummaryDownvote>
                      <IconX aria-hidden="true" />
                      {review.counterDownvote}
                      <ScreenReaderText>down votes</ScreenReaderText>
                    </SummaryDownvote>
                  </FlexRow>
                </FlexRow>
              </ReviewSummaryMain>
              {!!review.downvoteFeedback?.length && (
                <LighterText size="xs">
                  Feedback: {reviewFeedback(review.downvoteFeedback)}
                </LighterText>
              )}
              <ContentToggle showLabel="Show review" hideLabel="Hide review">
                <Review
                  key={review.reviewId}
                  review={review}
                  showVotes={false}
                />
              </ContentToggle>
            </ReviewSummary>
          ))}
        </ReviewList>

        {showViewAllReviewButton && (
          <SButton variant="default" onClick={() => setShowAllReviews(true)}>
            View All Reviews
          </SButton>
        )}
      </ReviewsWrapper>
    </>
  );
}

const SButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing.sm,
}));

const SH2 = styled(H2)(({ theme }) => ({
  ...theme.fonts.body,
  fontWeight: "bold",
  marginBottom: 0,
}));

const ReviewsWrapper = styled.div({
  padding: "1.5rem 0",
});

const ReviewList = styled.ul({
  listStyleType: "none",
  margin: 0,
  padding: 0,
  "&>*::before": {
    content: '"\\200B"',
    position: "absolute",
  },
});

type ReviewSummaryProps = {
  status?: string;
};

const ReviewSummary = styled.li<ReviewSummaryProps>(({ status, theme }) => ({
  alignItems: "stretch",
  border: `1px solid ${theme.colors.gray20}`,
  display: "flex",
  flexDirection: "column",
  gap: "1em",
  justifyContent: "center",
  padding: "1em",
  "&>*": {
    flex: "0 0 100%",
  },
  "&:first-of-type": {
    borderTopRightRadius: 10,
    borderTopLeftRadius: 10,
  },
  "&:last-of-type": {
    borderBottomRightRadius: 10,
    borderBottomLeftRadius: 10,
  },
  "&+&": {
    borderTop: 0,
  },
  "&>h3": {
    margin: 0,
  },
  p: {
    margin: 0,
  },
  ...(status === "APPROVED" && {
    ".SummaryUpvote": {
      backgroundColor: theme.colors.green,
    },
  }),
  ...(status === "REJECTED" && {
    ".SummaryDownvote": {
      backgroundColor: hexToRGB(theme.colors.primary, 0.33),
    },
  }),
}));
ReviewSummary.defaultProps = { className: "ReviewSummary" };

const SH3 = styled(H3)({
  alignItems: "center",
  display: "flex",
  flexWrap: "wrap",
  gap: "1em",
});
const ReviewSummaryMain = styled(FlexRow)({
  "@media (max-width: 600px)": {
    flexDirection: "column",
    alignItems: "flex-start",
  },
});

const Status = styled(Text)({
  textTransform: "capitalize",
  fontWeight: "bold",
});

const LighterText = styled(Text)({
  opacity: ".75",
});

const SummaryVote = styled.div(({ theme }) => ({
  alignItems: "center",
  border: `1px solid ${theme.colors.fg}`,
  borderRadius: theme.borderRadius.round,
  display: "inline-flex",
  flex: "0 0 auto",
  fontSize: theme.fontSizes.xs,
  fontWeight: "bold",
  gap: ".5em",
  justifyContent: "center",
  lineHeight: "1.2",
  minWidth: 50,
  width: "max-content",
  padding: ".2em .66em",
  svg: {
    height: ".6em",
    width: "auto",
  },
}));

const SummaryUpvote = styled(SummaryVote)(({ theme }) => ({}));
SummaryUpvote.defaultProps = { className: "SummaryUpvote" };

const SummaryDownvote = styled(SummaryVote)(({ theme }) => ({}));
SummaryDownvote.defaultProps = { className: "SummaryDownvote" };

export { ProfileReviews, ReviewSummary };
